/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-users
 * @responsibility Serialize the SET-F05 Users-tab wire calls onto IAM-F10's existing member operations.
 * @state-owner authenticated API
 * @allowed-imports API client public type; @vispo/contracts
 * @forbidden React; UI role inference; email/provider adapters
 * @public-api listOrganizationMembers, createOrganizationMember, updateOrganizationMember
 * @side-effects authenticated HTTP requests
 * @tests frontend/packages/workspace-web/src/modules/settings-users/components/UsersSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import type { VispoApiClient } from '@vispo/api-client';
import {
  type OrganizationMemberCreateInput,
  type OrganizationMemberUpdateInput,
  organizationMemberCommandResponseSchema,
  organizationMemberCreateInputSchema,
  organizationMemberDirectorySchema,
  organizationMemberUpdateInputSchema,
} from '@vispo/contracts';

const membersPath = (organizationId: string) =>
  `/api/v1/organizations/${encodeURIComponent(organizationId)}/members`;

export function listOrganizationMembers(apiClient: VispoApiClient, organizationId: string) {
  return apiClient
    .request<unknown>(membersPath(organizationId))
    .then((response) => organizationMemberDirectorySchema.parse(response));
}

export function createOrganizationMember(
  apiClient: VispoApiClient,
  organizationId: string,
  input: OrganizationMemberCreateInput,
) {
  return apiClient
    .request<unknown>(membersPath(organizationId), {
      method: 'POST',
      body: organizationMemberCreateInputSchema.parse(input),
    })
    .then((response) => organizationMemberCommandResponseSchema.parse(response));
}

export function updateOrganizationMember(
  apiClient: VispoApiClient,
  organizationId: string,
  userId: string,
  input: OrganizationMemberUpdateInput,
) {
  return apiClient
    .request<unknown>(`${membersPath(organizationId)}/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: organizationMemberUpdateInputSchema.parse(input),
    })
    .then((response) => organizationMemberCommandResponseSchema.parse(response));
}
